import { colorBackgroundClasses, TagColors } from '../../../atoms'
import {
    getStickyLeftPosition,
    getStickyRightPosition,
} from './helpers/stickyColumnHelpers'
import { TableColumnWidthsContext, TableContext } from '../../../../providers'
import { TableRowContext } from '../../../../providers/table/table-row-provider'
import { Column } from '../../../../types'
import { useRef, useState, useEffect, useContext } from 'react'

interface StickyStyling {
    left?: string
    right?: string
}

export const useStickyColumn = (column?: Column) => {
    const ref = useRef(null)
    const [styling, setStyling] = useState<StickyStyling>({})
    const { columns, hasBackground } = useContext(TableContext)
    const columnWidths = useContext(TableColumnWidthsContext)
    const { color: rowColor } = useContext(TableRowContext)

    const isStickyLeft = (className: string) => className.includes('left')
    const isStickyRight = (className: string) => className.includes('right')
    const isStickyBorderless = (className: string) => className.includes('borderless')

    const setStickyPosition = () => {
        if (!column?.sticky || !ref?.current || !columnWidths) {
            return
        }

        const newStyling: StickyStyling = {}
        if (isStickyLeft(column.sticky)) newStyling.left = getStickyLeftPosition(column, columns, columnWidths) ?? ''
        if (isStickyRight(column.sticky)) newStyling.right = getStickyRightPosition(column, columns, columnWidths) ?? ''

        setStyling(newStyling)
    }

    const isLastLeftStickyColumn = (column: Column): boolean =>
        columns.filter((column) => column.sticky?.includes('left')).at(-1)?.id === column.id
    const isFirstRightStickyColumn = (column: Column): boolean =>
        columns.filter((column) => column.sticky?.includes('right')).at(0)?.id === column.id

    const getStickyClasses = (classPrefix: string, color?: TagColors): string => {
        if (!column?.sticky) return ''
        
        let classes = ''
        classes += `${classPrefix}--sticky`
        if (isStickyLeft(column.sticky)) {
            classes += ` ${classPrefix}--sticky-left`
            if (isLastLeftStickyColumn(column)) classes += ` ${classPrefix}--sticky-left-last`
        }
        if (isStickyRight(column.sticky)) {
            classes += ` ${classPrefix}--sticky-right`
            if (isFirstRightStickyColumn(column)) classes += ` ${classPrefix}--sticky-right-first`
        }

        if (isStickyBorderless(column.sticky)) classes += ` ${classPrefix}--sticky__borderless`

        const backgroundColor = color || rowColor
        if (backgroundColor) classes += ` ${colorBackgroundClasses[backgroundColor]}`
        const defaultBackground = hasBackground ? ` ${classPrefix}--background-default` : ` ${classPrefix}--no-background-default-sticky-background`
        if (!backgroundColor) classes += defaultBackground

        return classes
    }

    /**
     * Non-sticky: stable sentinel so the effect does not run on every `columnWidths` update.
     * Sticky: serialized key so `undefined`, `[]`, and real measurements are distinct (same string as `[]` and
     * `undefined` would skip a needed re-run) and we do not depend on the array reference identity.
     */
    const stickyLayoutEffectDependency = column?.sticky
        ? columnWidths === undefined
            ? 'pending'
            : `${columnWidths.length}:${columnWidths.map((item) => `${item.position}:${item.width}`).join('|')}`
        : 'non-sticky'

    useEffect(() => {
        if (!column?.sticky) return
        setStickyPosition()
    }, [ref, stickyLayoutEffectDependency, column?.sticky])

    return {
        getStickyClasses,
        setStickyPosition,
        ref,
        styling,
    }
}
